import { yupResolver } from "@hookform/resolvers/yup"
import { Button, Image, Typography } from "@jewlr/storybook/core"
import { FloatInput, Switch } from "@jewlr/storybook/form"
import PropTypes from "prop-types"
import React from "react"
import { useForm, Controller } from "react-hook-form"
import styled, { withTheme } from "styled-components"
import isEmail from "validator/lib/isEmail"
import * as Yup from "yup"

import { history } from "app-store-v2"
import { openModal } from "components/modal"
import withAuth from "hoc/account/with-auth"

import {
  Cta,
  FieldContainer,
  ForgotPassword,
  RememberMeContainer,
  Footnote,
  HaveAccount,
  LogoContainer,
  LogoButton,
  Separator,
} from "./components"

const CreateAccount = styled(Button).attrs({
  mb: 2,
  outline: "black",
  py: 1,
  width: "100%",
})`
  font-size: 14px;
  font-weight: 500;
  height: 44px;
  line-height: 16px;
`

const schema = Yup.object().shape({
  email: Yup.string()
    .required("Please enter a valid email address.")
    .test("test-email", "Please enter a valid email address.", (value) =>
      isEmail(value || "")
    ),
  password: Yup.string().required("Please enter your password."),
})

const LoginForm = ({
  doFacebookLogin,
  regularLogin,
  setModalContent,
  theme,
}) => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      email: "",
      password: "",
      remember_me: true,
    },
    mode: "onBlur",
    resolver: yupResolver(schema),
  })

  const onSubmit = async (data) => {
    try {
      await new Promise((res, rej) => {
        regularLogin(data, {
          fromModal: !!setModalContent,
          onError: (err) => rej(err),
          onSuccess: () => res(),
        })
      })
    } catch (err) {
      setError("root.loginError", { message: err })
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {errors?.root?.loginError && (
        <Typography color="error" mb={2}>
          {errors.root.loginError.message}
        </Typography>
      )}
      <FieldContainer>
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <FloatInput
              autoComplete="email"
              data-cy="login-email-input"
              error={error}
              label="Email"
              placeholder="Enter Your Email"
              type="email"
              {...field}
            />
          )}
          rules={{ required: true }}
        />
      </FieldContainer>
      <FieldContainer>
        <Controller
          control={control}
          name="password"
          render={({ field, fieldState: { error } }) => (
            <FloatInput
              autoComplete="password"
              data-cy="login-password-input"
              error={error}
              label="Password"
              placeholder="Enter Your Password"
              type="password"
              {...field}
            />
          )}
          rules={{ required: true }}
        />
      </FieldContainer>
      <RememberMeContainer pt={1}>
        <Controller
          control={control}
          name="remember_me"
          render={({ field: { value, ...rest }, fieldState: { error } }) => (
            <Switch
              checked={value}
              error={error}
              id="remember_me"
              label="Remember Me"
              labelStyle={theme.loginForm.rememberMeLabel}
              {...rest}
            />
          )}
          rules={{ required: true }}
        />
        <ForgotPassword
          data-cy="login-forgot-password"
          onClick={() => openModal("#forgot-password")}
        >
          Forgot your password?
        </ForgotPassword>
      </RememberMeContainer>
      <Cta
        data-cy="login-sign-in-button"
        loading={isSubmitting}
        mb={2}
        type="submit"
      >
        Sign In
      </Cta>
      <Separator mb={2}>or sign up using</Separator>
      <LogoContainer>
        <LogoButton onClick={(e) => doFacebookLogin(e)}>
          <Image
            alt="Sign in with Facebook"
            src={require("images/icons/facebook.png")}
          />
        </LogoButton>
      </LogoContainer>
      <HaveAccount mb={2}>Don&apos;t have an account?</HaveAccount>
      <CreateAccount
        onClick={() =>
          setModalContent ? setModalContent("#signup") : history.push("/signup")
        }
      >
        Create Account
      </CreateAccount>
      <Footnote>
        Note: Creating an account is not required to purchase from{" "}
        {sl.config.site_title}.
      </Footnote>
    </form>
  )
}

LoginForm.propTypes = {
  doFacebookLogin: PropTypes.func,
  regularLogin: PropTypes.func,
  setModalContent: PropTypes.func,
  theme: PropTypes.object,
}

export default withAuth(withTheme(LoginForm))
